import { useMutation, useQueryClient } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import type { BookingInvitationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Host-only: invite a player to EVERY slot in the booking's recurring group
 * (POST /facilities/{facilityId}/bookings/{bookingId}/invitations/batch). Booking
 * N time-slots = N bookings sharing one `recurring_group_id`; this single host
 * action fans the invite across every slot (the backend treats a single booking as
 * a group-of-one, so the dialog ALWAYS goes through batch — never the single-slot
 * create). The body carries ONLY the invitee's user_id; the host is the token user
 * (server-derived). An invite does NOT add a roster row — it creates PENDING
 * invitations the invitee later accepts.
 *
 * On success the booking's pending-invitations list is INVALIDATED so the
 * exclusion set re-reads server truth — never an optimistic fabricated row. Errors
 * surface on `mutation.error` for the dialog to map honestly: a 409 duplicate
 * (already invited) → an inline "already invited" — never a fake success.
 */
export function useCreateBookingInvitation(
  facilityId: string,
  bookingId: string,
) {
  const queryClient = useQueryClient();
  return useMutation<BookingInvitationResponse[], unknown, string>({
    mutationFn: (inviteeId: string) =>
      facilityApi.createBookingInvitationsForGroup(facilityId, bookingId, {
        invitee_id: inviteeId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.booking.invitations(facilityId, bookingId),
      });
    },
  });
}
